import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import ErrorBoundary from "../common/ErrorBoundary";
import adminUserService from "../../services/adminUserService";

/**
 * Shell for /admin/dash/users/:userId.
 *
 * Deliberately light: just a breadcrumb and the tab bar. The Overview tab is
 * the existing UserIntelligenceLedger, which renders its own rich profile
 * header — duplicating that here would give the page two headers.
 *
 * It owns one shared thing: `deviceIndex`. Every cross-module link needs to
 * turn a VENDOR deviceId (what Schedule.actions[].deviceId stores) into a
 * Device._id (what the device route takes), and fetching that per tab would
 * mean three copies of the same list.
 */
const TABS = [
  { to: ".", label: "Overview", end: true },
  { to: "schedules", label: "Schedules" },
  // "What will actually fire, when" — a different question from "what rules
  // exist", and the one you have when a light switched at the wrong time.
  { to: "upcoming", label: "Upcoming" },
  { to: "devices", label: "Devices" },
  { to: "rooms", label: "Rooms" },
  // Archived schedules live in their own tab, not as a filter on Schedules:
  // it orders by archivedAt and its columns (what it WAS, when it was archived)
  // are noise on the live list.
  { to: "archive", label: "Archive" },
];

export default function UserDetailsLayout() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [deviceIndex, setDeviceIndex] = useState({ byVendorId: {}, byId: {}, all: [] });
  const [indexState, setIndexState] = useState("idle");

  const loadDeviceIndex = useCallback(async () => {
    if (!userId) return;
    setIndexState("loading");
    try {
      setDeviceIndex(await adminUserService.fetchDeviceIndex(userId));
      setIndexState("success");
    } catch (err) {
      // A failed index is not fatal: every consumer falls back to showing the
      // raw vendor id as un-clickable text rather than inventing a link.
      console.error("Failed to build device index:", err);
      setIndexState("error");
    }
  }, [userId]);

  useEffect(() => {
    loadDeviceIndex();
  }, [loadDeviceIndex]);

  return (
    <div className="p-4 md:p-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
      >
        <FiArrowLeft size={16} />
        Back
      </button>

      <nav className="mb-6 flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => (
          <NavLink
            key={tab.label}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `whitespace-nowrap px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                isActive
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* A crash in one tab must not blank the whole shell. */}
      <ErrorBoundary>
        <Outlet context={{ userId, deviceIndex, indexState, reloadDeviceIndex: loadDeviceIndex }} />
      </ErrorBoundary>
    </div>
  );
}
